import React, { useState, useMemo, useCallback } from 'react';
import { Modal, Input, Popover } from 'antd';
import { SearchOutlined, EllipsisOutlined } from '@ant-design/icons';
import dayjs from 'utils/dayjs';
import { TPreviousVisitNote } from 'types/treatment';
import useTimezone from 'hooks/useTimezone';
import s from './s.module.less';

type Props = {
    previousNotes: TPreviousVisitNote[];
    loading: boolean;
    onChange?: (note: TPreviousVisitNote) => void;
    disabled?: boolean;
};

const PreviousNotesSelection = ({
    previousNotes,
    loading,
    onChange,
    disabled,
}: Props) => {
    const [, myTimezone, isDifferentTimezoneWithBrowser] = useTimezone();
    const tz = (myTimezone?.value as string) || dayjs.tz.guess();
    const tzLabel = isDifferentTimezoneWithBrowser ? ` (${tz})` : '';

    const formatVisitDate = useCallback((visitStartAt: string) => {
        return dayjs.utc(visitStartAt).tz(tz).format('MMMM D');
    }, [tz]);

    const formatVisitRow = useCallback((note: TPreviousVisitNote) => {
        const start = dayjs.utc(note.visitStartAt).tz(tz);
        const datePart = start.format('dddd, MMMM D, YYYY');
        const startTime = start.format('h:mm');
        if (note.visitEndAt) {
            const end = dayjs.utc(note.visitEndAt).tz(tz);
            const endTime = end.format('h:mm a');
            return `${datePart} | ${startTime} - ${endTime}${tzLabel}`;
        }
        const startTimeWithPeriod = start.format('h:mm a');
        return `${datePart} | ${startTimeWithPeriod}${tzLabel}`;
    }, [tz, tzLabel]);

    const [popoverOpen, setPopoverOpen] = useState(false);
    const [searchText, setSearchText] = useState('');

    const mostRecent = previousNotes[0];

    const filteredNotes = useMemo(() => {
        if (!searchText.trim()) return previousNotes;
        const lower = searchText.toLowerCase().trim();
        return previousNotes.filter((note) => {
            const specialty = (note.specialty || '').toLowerCase();
            const visitType = (note.visitType || '').toLowerCase();
            const dateStr = formatVisitRow(note).toLowerCase();
            return specialty.includes(lower) || visitType.includes(lower) || dateStr.includes(lower);
        });
    }, [previousNotes, searchText, formatVisitRow]);

    if (loading || !mostRecent) {
        return null;
    }

    const handleConfirmAndApply = (note: TPreviousVisitNote) => {
        Modal.confirm({
            title: 'Start from previous visit notes?',
            content: 'This will replace the content you\'ve already entered in the current visit notes.',
            okText: 'Confirm',
            onCancel: () => {},
            icon: null,
            width: 500,
            okButtonProps: {
                className: s.confirmButton,
            },
            onOk: () => {
                onChange?.(note);
                setPopoverOpen(false);
            },
            closable: true,
            wrapClassName: s.customConfirmRoot,
        });
    };

    const handleMainButtonClick = () => {
        if (disabled) return;
        handleConfirmAndApply(mostRecent);
    };

    const handleNoteSelect = (note: TPreviousVisitNote) => {
        handleConfirmAndApply(note);
    };

    const popoverContent = (
        <div className={s.popoverContent}>
            <Input
                prefix={<SearchOutlined className={s.searchIcon} />}
                placeholder="Search"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                className={s.searchInput}
                allowClear
            />
            <div className={s.notesList}>
                {filteredNotes.map((note) => (
                    <div
                        key={`${note.businessType}-${note.businessId}`}
                        className={s.noteItem}
                        onClick={() => handleNoteSelect(note)}
                    >
                        <div className={s.noteTitle}>
                            {note.specialty || note.visitType
                                ? `${note.specialty || ''}${note.specialty && note.visitType ? ': ' : ''}${note.visitType || ''}`
                                : 'Visit note'}
                        </div>
                        <div className={s.noteDate}>
                            {formatVisitRow(note)}
                        </div>
                    </div>
                ))}
                {filteredNotes.length === 0 && (
                    <div className={s.emptyState}>No matching visit notes</div>
                )}
            </div>
        </div>
    );

    return (
        <div className={s.wrapper}>
            <span
                className={`${s.mainButton} ${disabled ? s.disabled : ''}`}
                onClick={handleMainButtonClick}
            >
                {`Start from ${formatVisitDate(mostRecent.visitStartAt)}'s visit notes`}
            </span>
            {previousNotes.length > 1 && (
                <Popover
                    open={popoverOpen}
                    onOpenChange={(open) => {
                        if (!disabled) {
                            setPopoverOpen(open);
                            if (!open) setSearchText('');
                        }
                    }}
                    content={popoverContent}
                    trigger="click"
                    placement="bottomRight"
                    overlayClassName={s.popoverOverlay}
                >
                    <span
                        className={`${s.moreButton} ${disabled ? s.disabled : ''}`}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <EllipsisOutlined rotate={90} />
                    </span>
                </Popover>
            )}
        </div>
    );
};

export default PreviousNotesSelection;
